import React, { Component } from "react";
import { NavLink, RouteComponentProps, withRouter } from "react-router-dom";
import { FiChevronsLeft, FiChevronsRight, FiStar, FiUser } from "react-icons/fi";
import { NAV_ENTRIES, NavEntry, NavItem } from "../Helper/Constant";
import { PermissionsContext, PermissionsState, RouteGuard } from "../Auth/PermissionsContext";
import { withStore } from "../Redux/Index";
import { fetchContentTypes } from "../Redux/Slice/ContentTypesSlice";
import { fetchTaxonomies } from "../Redux/Slice/TaxonomiesSlice";
import { fetchCategories } from "../Redux/Slice/CategoriesSlice";
import { fetchContentTypeConfigs } from "../Redux/Slice/ContentTypeConfigsSlice";
import PageHeader from "./PageHeader";
import { VersionWatcher } from "./VersionWatcher";
import { PreviewAccessCodeLoader } from "./PreviewAccessCodeLoader";
import { SidebarLogout } from "./SidebarLogout";
import { PageHeaderActionsProvider } from "./PageHeaderActionsProvider";
import RouteHeaderSync from "./RouteHeaderSync";
import { TicketNavBadge } from "../Realtime/TicketNavBadge";
import { RedirectNavBadge } from "../Realtime/RedirectNavBadge";
import { ContactNavBadge } from "../Realtime/ContactNavBadge";
import { useRealtime } from "../Realtime/RealtimeProvider";

/** Statut de connexion temps réel dans le pied de la sidebar (section Mon compte) : *  l'ancien pastillage « N en ligne » de la barre supérieure vit désormais ici. */
const SidebarPresence: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => {
	const { connected, online } = useRealtime();
	const label = connected ? `${online} en ligne` : "Hors ligne";
	return (
		<div
			className={`flex items-center gap-3 rounded-lg px-3 py-1.5 text-xs font-semibold ${connected ? "text-emerald-700" : "text-slate-400"} ${isCollapsed ? "justify-center px-2" : ""}`}
			title={label}
			aria-label={label}
		>
			<span className={`h-2 w-2 shrink-0 rounded-full ${connected ? "animate-pulse bg-emerald-500" : "bg-slate-300"}`} />
			{!isCollapsed && <span>{label}</span>}
		</div>
	);
};

export interface State {
	isCollapsed: boolean;
}

interface Props extends RouteComponentProps<{}> {
	children?: React.ReactNode;
	contentTypesInitialized: boolean;
	contentTypesLoading: boolean;
	fetchContentTypes: () => void;
	taxonomiesInitialized: boolean;
	taxonomiesLoading: boolean;
	fetchTaxonomies: () => void;
	categoriesInitialized: boolean;
	categoriesLoading: boolean;
	fetchCategories: () => void;
	contentTypeConfigsInitialized: boolean;
	contentTypeConfigsLoading: boolean;
	fetchContentTypeConfigs: () => void;
	contentTypeConfigsItems: Array<{ id?: string | null; title?: string | null; isMain?: boolean | null }>;
}

/** Main application layout with navigation sidebar, header and content area. */
class Layout extends Component<Props, State> {
	static displayName = Layout.name;
	// Token-derived permissions drive which sidebar items render (the user never sees a page
	// they cannot open). Consumed via legacy context (this.context is read + cast in buildVisibleNav).
	static contextType = PermissionsContext;
	/** Sidebar starts collapsed below this width and tracks crossings of it. */
	private static readonly COLLAPSE_BELOW = 1024;
	private static isSmallViewport = () =>
		typeof window !== "undefined" && window.innerWidth < Layout.COLLAPSE_BELOW;
	private lastIsSmall = false;

	constructor(props: any) {
		super(props);
		// Collapsed by default on small screens; expanded on the desktop.
		this.lastIsSmall = Layout.isSmallViewport();
		this.state = {
			isCollapsed: this.lastIsSmall
		};
	}

	/**
	 * Collapse when the viewport crosses into "small" and expand when it crosses back
	 * : only on the transition, so a manual toggle within a size band is preserved.
	 */
	private handleResize = () => {
		const small = Layout.isSmallViewport();
		if (small !== this.lastIsSmall) {
			this.lastIsSmall = small;
			this.setState({ isCollapsed: small });
		}
	};

	public clickToggle = () => {
		this.setState((prev) => ({
			isCollapsed: !prev.isCollapsed
		}));
	};

	componentDidMount(): void {
		window.addEventListener("resize", this.handleResize);
		if (!this.props.contentTypesInitialized && !this.props.contentTypesLoading) {
			this.props.fetchContentTypes();
		}
		if (!this.props.taxonomiesInitialized && !this.props.taxonomiesLoading) {
			this.props.fetchTaxonomies();
		}
		if (!this.props.categoriesInitialized && !this.props.categoriesLoading) {
			this.props.fetchCategories();
		}
		if (!this.props.contentTypeConfigsInitialized && !this.props.contentTypeConfigsLoading) {
			this.props.fetchContentTypeConfigs();
		}
	}

	componentWillUnmount(): void {
		window.removeEventListener("resize", this.handleResize);
	}

	/** Filter NAV_ENTRIES down to what the current admin may see: drop items whose route the
	 * user lacks read access to, hide the whole Content group if they cannot read content, and
	 * drop section headers that end up with no visible items beneath them. */
	private buildVisibleNav(mainContentTypes: NavItem[]): NavEntry[] {
		const perms = this.context as PermissionsState | null;
		const allowed = (to: string) => !perms || perms.routeAllowed(to);

		const filtered: NavEntry[] = [];
		for (const entry of NAV_ENTRIES) {
			if (entry.type === "section") {
				filtered.push(entry);
			} else if (entry.type === "item") {
				if (allowed(entry.to)) filtered.push(entry);
			} else {
				// group : hide entirely if its landing route is not allowed, else keep visible children.
				if (!allowed(entry.to)) continue;
				const merged = entry.key === "content" ? entry.children.concat(mainContentTypes) : entry.children;
				filtered.push({ ...entry, children: merged.filter((c) => allowed(c.to)) });
			}
		}

		// Drop a section header when nothing visible follows it before the next section.
		const pruned: NavEntry[] = [];
		for (let i = 0; i < filtered.length; i++) {
			const e = filtered[i];
			if (e.type === "section") {
				const next = filtered[i + 1];
				if (next && next.type !== "section") pruned.push(e);
			} else {
				pruned.push(e);
			}
		}
		return pruned;
	}

	render() {
		const { isCollapsed } = this.state;
		const mainContentTypes: NavItem[] = (this.props.contentTypeConfigsItems ?? [])
			.filter((item) => item?.isMain && item?.title)
			.map((item) => ({
				type: "item" as const,
				to: `/content/${encodeURIComponent(item.id ?? normalizeContentTypeKey(item.title ?? ""))}`,
				label: item.title ?? "Content type",
				icon: FiStar
			}));
		const visibleNav = this.buildVisibleNav(mainContentTypes);
		return (
			<div className="min-h-screen h-screen flex bg-slate-50" id="wrapper">
				<aside
					className={`relative z-[1000] shrink-0 border-r border-slate-200 bg-white/95 backdrop-blur flex flex-col transition-all duration-300 ease-in-out ${
						isCollapsed ? "w-20" : "w-72"
					}`}
				>
					<button
						type="button"
						onClick={this.clickToggle}
						aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
						className={`absolute -right-3 top-6 z-[999] flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 ${
							isCollapsed ? "rotate-180" : ""
						}`}
					>
						{isCollapsed ? <FiChevronsRight className="h-4 w-4" /> : <FiChevronsLeft className="h-4 w-4" />}
					</button>
					<div
						className={`flex items-center gap-4 border-b border-slate-200 ${isCollapsed ? "px-3 py-4 justify-center" : "px-6 py-5"}`}
					>
						<img
							src="/BCC Logo - small.png"
							alt="BCC logo"
							className={`rounded-2xl object-contain transition-all duration-300 ${isCollapsed ? "h-10 w-10" : "h-14 w-14"}`}
						/>
						{!isCollapsed && (
							<div className="min-w-0">
								<p className="text-base font-semibold uppercase tracking-[0.3em] text-slate-800">BCC Admin</p>
							</div>
						)}
					</div>
					<nav className={`flex-1 overflow-auto ${isCollapsed ? "px-2 py-4" : "px-3 py-6"} space-y-2`}>
						{visibleNav.map((entry, index) => {
							if (entry.type === "section") {
								if (isCollapsed) return null;
								return (
									<p
										key={`section-${entry.label}-${index}`}
										className="px-4 pt-6 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400"
									>
										{entry.label}
									</p>
								);
							}

							if (entry.type === "group") {
								// Flat & non-collapsable: the parent ("Content") and its children : Pages, Layout,
								// Languages, Contact Info + the main content types (Article, Event…) appended at
								// runtime : all render as ordinary, equally-weighted nav items directly below it.
								// Children were already merged with the main content types and filtered by
								// permission in buildVisibleNav : render them as-is.
								const groupChildren = entry.children;
								const navItemClass = `flex items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold text-slate-600 transition hover:bg-slate-100 ${
									isCollapsed ? "justify-center px-3" : ""
								}`;
								return (
									<React.Fragment key={`group-${entry.key}`}>
										{[{ type: "item" as const, to: entry.to, label: entry.label, icon: entry.icon }, ...groupChildren].map(
											(item) => {
												const ItemIcon = item.icon;
												return (
													<NavLink
														key={item.to}
														to={item.to}
														exact
														activeClassName="bg-slate-900 text-white shadow-md hover:bg-slate-900"
														className={navItemClass}
													>
														<ItemIcon className="h-5 w-5" />
														<span className={isCollapsed ? "hidden" : "block"}>{item.label}</span>
													</NavLink>
												);
											}
										)}
									</React.Fragment>
								);
							}

							const Icon = entry.icon;
							return (
								<NavLink
									key={entry.to}
									to={entry.to}
									exact
									activeClassName="bg-slate-900 text-white shadow-md hover:bg-slate-900"
									className={`relative flex items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold text-slate-600 transition hover:bg-slate-100 ${
										isCollapsed ? "justify-center px-3" : ""
									}`}
								>
									<Icon className="h-5 w-5" />
									<span className={isCollapsed ? "hidden" : "block"}>{entry.label}</span>
									{entry.badge === "tickets" && <TicketNavBadge collapsed={isCollapsed} />}
									{entry.badge === "redirects" && <RedirectNavBadge collapsed={isCollapsed} />}
									{entry.badge === "contact" && <ContactNavBadge collapsed={isCollapsed} />}
								</NavLink>
							);
						})}
					</nav>
					{/* Sidebar footer: account + logout. Le statut « N en ligne » vit ICI (demande
					    utilisateur) ; la barre supérieure ne montre plus que les avatars, l'anneau
					    vert autour de sa propre photo signalant la connexion. */}
					<div className={`space-y-2 border-t border-slate-200 p-3 ${isCollapsed ? "px-2" : ""}`}>
						<SidebarPresence isCollapsed={isCollapsed} />
						{/* Every authenticated admin can reach their own account (profile, avatar,
						    password, personal activity) : sits directly above logout. */}
						<NavLink
							to="/account"
							title="Mon compte"
							className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 ${isCollapsed ? "justify-center px-2" : ""}`}
							activeClassName="bg-slate-100 text-slate-900"
						>
							<FiUser className="shrink-0" />
							{!isCollapsed && <span>Mon compte</span>}
						</NavLink>
						<SidebarLogout isCollapsed={isCollapsed} />
					</div>
				</aside>
				<div className="flex-1 flex flex-col min-w-0">
					<main className="flex-1 min-h-0 flex flex-col">
						<PageHeaderActionsProvider>
							<RouteHeaderSync />
							<VersionWatcher />
							<PreviewAccessCodeLoader />
							<PageHeader />
							<div className="flex-1 min-h-0 overflow-auto">
								<RouteGuard>{this.props.children}</RouteGuard>
							</div>
						</PageHeaderActionsProvider>
					</main>
				</div>
			</div>
		);
	}
}

const normalizeContentTypeKey = (value: string) =>
	(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const connectedComponent = withStore(
	Layout,
	(state) => ({
		contentTypesInitialized: state.contentTypes.initialized,
		contentTypesLoading: state.contentTypes.loading,
		taxonomiesInitialized: state.taxonomies.initialized,
		taxonomiesLoading: state.taxonomies.loading,
		categoriesInitialized: state.categories.initialized,
		categoriesLoading: state.categories.loading,
		contentTypeConfigsInitialized: state.contentTypeConfigs.initialized,
		contentTypeConfigsLoading: state.contentTypeConfigs.loading,
		contentTypeConfigsItems: state.contentTypeConfigs.items
	}),
	{
		fetchContentTypes,
		fetchTaxonomies,
		fetchCategories,
		fetchContentTypeConfigs
	}
);

export default withRouter(connectedComponent);
